import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Lock, ArrowLeft, Loader2, ShieldCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAthleteAuth } from '@/contexts/AthleteAuthContext';
import { api } from '@/hooks/useApi';

export default function AthleteChangePasswordPage() {
  const { token, setToken, refreshProfile, athlete, mustChangePassword } =
    useAthleteAuth();
  const navigate = useNavigate();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  // If already logged in and no password change required, redirect
  if (athlete && !mustChangePassword) {
    navigate('/athlete/dashboard', { replace: true });
    return null;
  }

  // If no token at all, go to login
  if (!token) {
    navigate('/athlete/login', { replace: true });
    return null;
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!currentPassword.trim() || !newPassword.trim() || !confirmPassword.trim()) {
      toast.error('請填寫所有欄位 / Please fill in all fields');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('新密碼至少需要8個字元 / New password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('新密碼與確認密碼不一致 / Passwords do not match');
      return;
    }

    if (currentPassword === newPassword) {
      toast.error('新密碼不能與舊密碼相同 / New password must differ from current password');
      return;
    }

    setLoading(true);
    try {
      const res = await api.put(
        '/athlete-auth/change-password',
        { currentPassword, newPassword },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const data = res.data?.data ?? res.data;

      // Store new token if provided
      if (data.token) {
        setToken(data.token);
      }

      toast.success('密碼變更成功 / Password changed successfully');

      // Refresh profile to clear mustChangePassword state
      await refreshProfile();
      navigate('/athlete/dashboard', { replace: true });
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? ((err as { response?: { data?: { message?: string } } }).response?.data
              ?.message ?? '密碼變更失敗 / Password change failed')
          : '密碼變更失敗 / Password change failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Back to login */}
        <Link
          to="/athlete/login"
          className="inline-flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>返回登入 / Back to login</span>
        </Link>

        {/* Card */}
        <div className="bg-[var(--bg-card)] rounded-xl shadow-lg border border-[var(--border)] p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-500 text-white flex items-center justify-center">
              <ShieldCheck className="w-8 h-8" />
            </div>
            <h1 className="text-2xl font-bold text-[var(--text-primary)]">變更密碼</h1>
            <p className="text-sm text-[var(--text-secondary)] mt-1">Change Password</p>
            <p className="mt-3 text-sm text-amber-600 bg-amber-50 rounded-lg px-4 py-2">
              首次登入需變更密碼才能繼續使用
              <br />
              <span className="text-xs">
                You must change your password on first login to continue.
              </span>
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label
                htmlFor="currentPassword"
                className="block text-sm font-medium text-[var(--text-primary)] mb-1.5"
              >
                目前密碼{' '}
                <span className="text-xs text-[var(--text-secondary)]">/ Current Password</span>
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-secondary)]" />
                <input
                  id="currentPassword"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-[var(--border)] bg-[var(--bg-primary)] text-[var(--text-primary)] placeholder:text-[var(--text-secondary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:border-transparent transition-shadow"
                  placeholder="請輸入目前密碼"
                  autoComplete="current-password"
                  disabled={loading}
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="newPassword"
                className="block text-sm font-medium text-[var(--text-primary)] mb-1.5"
              >
                新密碼{' '}
                <span className="text-xs text-[var(--text-secondary)]">/ New Password</span>
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-secondary)]" />
                <input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-[var(--border)] bg-[var(--bg-primary)] text-[var(--text-primary)] placeholder:text-[var(--text-secondary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:border-transparent transition-shadow"
                  placeholder="至少8個字元"
                  autoComplete="new-password"
                  disabled={loading}
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-[var(--text-primary)] mb-1.5"
              >
                確認新密碼{' '}
                <span className="text-xs text-[var(--text-secondary)]">/ Confirm New Password</span>
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-secondary)]" />
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-[var(--border)] bg-[var(--bg-primary)] text-[var(--text-primary)] placeholder:text-[var(--text-secondary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:border-transparent transition-shadow"
                  placeholder="再次輸入新密碼"
                  autoComplete="new-password"
                  disabled={loading}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-lg bg-[var(--primary)] text-white font-semibold hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>變更中... / Changing...</span>
                </>
              ) : (
                <span>確認變更 / Change Password</span>
              )}
            </button>
          </form>
        </div>

        {/* Event info */}
        <p className="mt-6 text-center text-xs text-[var(--text-secondary)]">
          第14屆全國泰雅族運動會暨第3屆新竹縣原住民族傳統技藝競賽
          <br />
          14th National Atayal Games & 3rd Hsinchu County Indigenous Games
        </p>
      </div>
    </div>
  );
}
